import React from 'react';
import { Box } from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedTextField as TextField } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import { Refresh } from '@mui/icons-material';

interface AuthCaptchaProps {
  challengeText: string;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  onRefresh: () => void;
}

const AuthCaptcha: React.FC<AuthCaptchaProps> = ({
  challengeText,
  value,
  error,
  onChange,
  onRefresh
}) => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !challengeText) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#f3faf6';
    context.fillRect(0, 0, canvas.width, canvas.height);

    for (let i = 0; i < 5; i += 1) {
      context.strokeStyle = `rgba(46, 125, 87, ${0.15 + Math.random() * 0.2})`;
      context.beginPath();
      context.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      context.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      context.stroke();
    }

    const charWidth = canvas.width / (challengeText.length + 1);
    context.font = 'bold 28px sans-serif';
    context.textBaseline = 'middle';

    challengeText.split('').forEach((char, index) => {
      const x = charWidth * (index + 1);
      const y = canvas.height / 2 + (Math.random() * 10 - 5);
      const rotation = (Math.random() * 0.5) - 0.25;

      context.save();
      context.translate(x, y);
      context.rotate(rotation);
      context.fillStyle = index % 2 === 0 ? '#236c4d' : '#1d5a40';
      context.fillText(char, -8, 0);
      context.restore();
    });
  }, [challengeText]);

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1
        }}
      >
        <Box>
          <Typography variant="subtitle2">Captcha</Typography>
          <Box
            sx={{
              mt: 0.75,
              p: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: '#f8fcfa',
              width: 'fit-content'
            }}
          >
            <canvas ref={canvasRef} width={220} height={64} />
          </Box>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Refresh />}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </Box>
      {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
      <TextField
        fullWidth
        label="Captcha Answer"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        margin="normal"
        required
      />
    </Box>
  );
};

export default AuthCaptcha;
